import { hasResource } from '@ctrl/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

import { OrderActions } from '@/components/orders/order-actions';
import { OrderDetail } from '@/components/orders/order-detail';
import { useDetailTitle } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import { ORDER_STATUS_META } from '@/lib/order-display';
import { isTerminal, nextStatuses } from '@/lib/order-transitions';
import {
  FALLBACK_TIMEZONE,
  getOrderDetail,
  getStoreTimeZone,
  type OrderStatus,
  updateOrderStatus,
} from '@/lib/orders';

/**
 * One order.
 *
 * Server-read for the same reasons the list is: the record is shopper PII and
 * the `orders` capability is enforced per request, so nothing about this order
 * is in the browser until somebody entitled to it asks for this id.
 */
export const Route = createFileRoute('/orders/$orderId')({
  component: OrderDetailPage,
});

function OrderDetailPage() {
  const { orderId } = useParams({ from: '/orders/$orderId' });
  const { activeAccountId, claims } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();

  /*
   * The gate the database actually applies.
   *
   * Updating an order is `('orders', 'member', 'member', 'member', …)` — any
   * member holding the `orders` capability, not `orders.manage`, which gates
   * deletion. Reaching for `can('orders.manage')` here would grey the control
   * out for the budtender the board exists for.
   */
  const canWork = accountId ? hasResource(claims, accountId, 'orders') : false;

  const timeZone = useQuery({
    queryKey: ['store-timezone', accountId],
    queryFn: () => getStoreTimeZone(accountId),
    enabled: Boolean(accountId),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const zone = timeZone.data ?? FALLBACK_TIMEZONE;

  const detail = useQuery({
    queryKey: ['order', accountId, orderId],
    queryFn: () => getOrderDetail(accountId, orderId),
    enabled: Boolean(accountId),
  });

  const order = detail.data?.order;

  // Publishes the third breadcrumb: [collapse | Orders | this record]. The
  // order number is what staff call it; the id is not a name anyone uses.
  useDetailTitle(order ? `Order #${order.order_number}` : `Order ${orderId.slice(0, 8)}`);

  const refresh = () => {
    // The list's counts and badges are now stale too, so drop both rather
    // than patching this record in place.
    queryClient.invalidateQueries({ queryKey: ['order', accountId, orderId] });
    queryClient.invalidateQueries({ queryKey: ['orders', accountId] });
  };

  const status = useMutation({
    mutationFn: (next: OrderStatus) => updateOrderStatus(accountId, orderId, next),
    onSuccess: () => {
      toast.success('Status updated.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (detail.isLoading || timeZone.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  /*
   * One answer for "no such order" and "not yours". Telling somebody an order
   * exists but belongs to another account is itself a disclosure, and the query
   * cannot tell the two apart anyway — RLS filters the row out either way.
   */
  if (!detail.data || !order) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-3 py-24 text-center">
        <ShoppingBag className="size-8" />
        <p className="text-foreground text-sm font-medium">No such order</p>
        <p className="max-w-sm text-sm">
          It may have been deleted, or it belongs to another account.
        </p>

        <Button asChild variant="outline" size="sm">
          <Link to="/orders">
            <ArrowLeft className="size-4" />
            Back to orders
          </Link>
        </Button>
      </div>
    );
  }

  const meta = ORDER_STATUS_META[order.status];
  const terminal = isTerminal(order.status);

  // The status control offers the current state plus the moves the machine
  // allows from it -- never the whole enum.
  const choices: OrderStatus[] = [
    order.status,
    ...nextStatuses(order.status, order.fulfillment_type),
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight tabular-nums">
            Order #{order.order_number}
          </h1>
          <Badge className={meta.className}>{meta.label}</Badge>
          {terminal && (
            <span className="text-muted-foreground text-xs">
              {order.status === 'canceled' && order.cancellation_reason
                ? `Canceled: ${order.cancellation_reason}`
                : 'Finished — locked.'}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={order.status}
            onValueChange={(value) => status.mutate(value as OrderStatus)}
            disabled={!canWork || status.isPending || terminal}
          >
            <SelectTrigger className="w-48" aria-label="Change status">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              {choices.map((value) => (
                <SelectItem key={value} value={value}>
                  {ORDER_STATUS_META[value].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <OrderActions accountId={accountId} detail={detail.data} onChanged={refresh} />
        </div>
      </div>

      {/* Print only: the heading the screen header carries. */}
      <h1 className="hidden text-xl font-semibold print:block">
        Order #{order.order_number} — {meta.label}
      </h1>

      <OrderDetail detail={detail.data} timeZone={zone} />
    </div>
  );
}
